import { Facebook, Instagram, Youtube, MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import FooterContact from "./FooterContact";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";
import { useContactInfo } from "@/hooks/useContactInfo";

const Footer = ({ showContactForm = false }: { showContactForm?: boolean }) => {
  const { t } = useLanguage();
  const { get } = useCmsContent();
  const contactInfo = useContactInfo();

  const navLinks = [
    { name: t.footer.home, href: "/" },
    { name: t.footer.about, href: "/about" },
    { name: t.footer.rooms, href: "/rooms" },
    { name: t.footer.restaurant, href: "/restaurant" },
    { name: t.footer.contact, href: "/contact" },
  ];

  const legalLinks = [
    { name: t.footer.impressum, href: "/impressum" },
    { name: t.footer.datenschutz, href: "/datenschutz" },
  ];

  const socialLinks = [
    { icon: Facebook, href: contactInfo?.facebook_url || "https://www.facebook.com/people/Esos-Hotel-Quelle/61580903713413/", label: "Facebook" },
    { icon: Instagram, href: contactInfo?.instagram_url || "https://www.instagram.com/esos_hotel_quelle/", label: "Instagram" },
    { icon: Youtube, href: "https://www.youtube.com/watch?v=0dR9_lVnfwo", label: "Youtube" },
    { icon: MessageCircle, href: "https://wa.me/41772581052", label: "WhatsApp" },
  ];

  const email = contactInfo?.email || "info@esoshotelquelle.com";
  const phone = contactInfo?.phone || "+41 81 302 11 13";

  return (
    <footer className="bg-card text-foreground">
      {showContactForm && <FooterContact />}
      <div className="hotel-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5 space-y-8">
            <Link to="/"><img src={logo} alt="Hotel Quelle Bad Ragaz" loading="lazy" className="h-14 w-auto" /></Link>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-accent shrink-0" />
                <p className="font-body text-sm text-muted-foreground leading-relaxed">
                  {contactInfo?.address || "Esos Hotel Quelle\nBadstrasse 9a\n7310 Bad Ragaz, St. Gallen, Schweiz"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-accent shrink-0" />
                <a href={`mailto:${email}`} className="font-body text-sm text-muted-foreground hover:text-accent transition-colors duration-300">
                  {email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-accent shrink-0" />
                <div className="font-body text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">{t.footer.reservation}: </span>
                  <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-accent transition-colors duration-300">{phone}</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-accent shrink-0" />
                <div className="font-body text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">Mobile: </span>
                  <a href="tel:+41772581052" className="hover:text-accent transition-colors duration-300">+41 77 258 10 52</a>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3 lg:col-start-7">
            <h4 className="hotel-caption text-foreground mb-6 tracking-[0.25em]">{t.footer.navigation}</h4>
            <nav aria-label="Footer Navigation">
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.name}><Link to={link.href} className="font-body text-sm text-muted-foreground hover:text-accent transition-colors duration-300">{link.name}</Link></li>
                ))}
              </ul>
            </nav>
          </div>
          <div className="lg:col-span-3">
            <h4 className="hotel-caption text-foreground mb-6 tracking-[0.25em]">{t.footer.followUs}</h4>
            <div className="flex gap-3 mb-10">
              {socialLinks.map((social) => (
                <a key={social.label} href={social.href} aria-label={social.label}
                  className="w-10 h-10 flex items-center justify-center border border-border rounded-full hover:bg-accent hover:border-accent hover:text-accent-foreground transition-all duration-300">
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <h4 className="hotel-caption text-foreground mb-4 tracking-[0.25em]">{t.footer.legal}</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}><Link to={link.href} className="font-body text-sm text-muted-foreground hover:text-accent transition-colors duration-300">{link.name}</Link></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="hotel-container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-xs text-muted-foreground">
              © {new Date().getFullYear()} Hotel Quelle Bad Ragaz. {t.footer.rights}
            </p>
            <p className="font-body text-xs text-muted-foreground">
              {t.footer.designBy}{" "}
              <a href="https://starkschild.ch" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors duration-300">Starkschild</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
